import React, { useState, useRef, useEffect } from 'react';
import { useLang } from '../../i18n';
import { ChevronDown, Check } from 'lucide-react';

export const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang, FLAGS, LANG_NAMES } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const languages = Object.keys(FLAGS).map(code => ({
    code,
    flag: FLAGS[code],
    name: LANG_NAMES[code]
  }));

  const currentLang = languages.find(l => l.code === lang) || languages[0];

  if (compact) {
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white bg-gray-800/50 rounded transition-colors"
          data-testid="lang-switcher-compact"
        >
          <span>{currentLang.flag}</span>
          <ChevronDown className="w-3 h-3" />
        </button>
        {open && (
          <div className="absolute top-full right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 min-w-[120px]">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-800 transition-colors ${lang === l.code ? 'text-red-500' : 'text-gray-300'}`}
                data-testid={`lang-option-${l.code}`}
              >
                <span>{l.flag}</span>
                <span>{l.name}</span>
                {lang === l.code && <Check className="w-3 h-3 ml-auto" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-800/50 rounded-lg transition-colors"
        data-testid="lang-switcher"
      >
        <span>{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.name}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-1 right-0 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 min-w-[140px]">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-800 transition-colors ${lang === l.code ? 'text-red-500' : 'text-gray-300'}`}
              data-testid={`lang-option-${l.code}`}
            >
              <span>{l.flag}</span>
              <span>{l.name}</span>
              {lang === l.code && <Check className="w-3.5 h-3.5 ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
